import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Section from './ui/Section';
import Container from './ui/Container';
import SectionHeading from './ui/SectionHeading';
import Reveal from './ui/Reveal';
import { ArrowRight } from './ui/Icons';
import PillarIcon from './PillarIcon';
import { pillars, secondaryOffers } from '../data/services';

const Grid = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.75rem;
  border-radius: var(--radius-md);
  background: var(--surface);
  border: 1px solid var(--border);
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-strong);
    transform: translateY(-2px);
  }
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(192, 204, 48, 0.1);
  border: 1px solid rgba(192, 204, 48, 0.25);
  color: var(--accent);
  margin-bottom: 1.25rem;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.6rem;
`;

const CardText = styled.p`
  color: var(--text-2);
  font-size: 0.95rem;
  flex-grow: 1;
`;

const More = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--accent);
`;

const AlsoLine = styled.p`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border);
  color: var(--text-2);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;

  strong {
    color: var(--text-1);
    font-weight: 600;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-height: 44px;
    color: var(--accent);
    font-weight: 600;
  }
`;

const ServicesGrid: React.FC = () => (
  <Section $tone="raised" aria-labelledby="services-title">
    <Container>
      <SectionHeading
        id="services-title"
        eyebrow="Services"
        title="What we build"
        description="Websites, mobile apps, cloud infrastructure, and the security around them, designed, built, and maintained by one team."
      />
      <Grid>
        {pillars.map((pillar, index) => (
          <Reveal as="li" key={pillar.id} delay={index * 0.06}>
            <Card to={`/services#${pillar.id}`}>
              <IconWrap>
                <PillarIcon id={pillar.id} />
              </IconWrap>
              <CardTitle>{pillar.title}</CardTitle>
              <CardText>{pillar.blurb}</CardText>
              <More>
                Learn more <ArrowRight size={16} />
              </More>
            </Card>
          </Reveal>
        ))}
      </Grid>
      <AlsoLine>
        <span>
          <strong>Also:</strong> {secondaryOffers.map((offer) => offer.title).join(', ')}.
        </span>
        <Link to="/services">
          All services <ArrowRight size={16} />
        </Link>
      </AlsoLine>
    </Container>
  </Section>
);

export default ServicesGrid;
